import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import { COPY, MACRO_LABEL } from '../copy';
import {
  axisLabel,
  bandFor,
  bandSpans,
  barAriaLabel,
  CHART_TITLE,
  chartHint,
  macroStatus,
  ratioPct,
  tooltipTitle,
} from '../lib/sufficiency';
import { MACROS } from '../types';
import type { Granularity, SufficiencyBucket } from '../types';
import { BandPill, HelpBody, InfoTip, MacroMark } from './parts';

/*
 * Drawn as plain SVG rather than through recharts: every bar has to be a
 * focusable button that opens the tooltip from the keyboard, carry its own
 * track and rounded clip, and turn into a "Not logged" placeholder, none of
 * which recharts' stacked Bar exposes without overriding all of it.
 */

const PAD_L = 30;
const PAD_R = 64; // room for the "Avg" pill and the "Medal" label
const TOP = 10;
const PLOT_H = 204;
const HEIGHT = PLOT_H + TOP + 40;
const TICKS = [0, 50, 70, 80, 100];
const MIN_SLOT: Record<Granularity, number> = { daily: 12, weekly: 40, monthly: 56 };
// Share of its slot a bar fills, and its widest size. Daily and weekly bars
// stay slim so many fit; monthly has only a few, so they go broad.
const BAR: Record<Granularity, { ratio: number; max: number }> = {
  daily: { ratio: 0.5, max: 8 },
  weekly: { ratio: 0.45, max: 28 },
  monthly: { ratio: 0.7, max: 120 },
};
const TIP_W = 220;
const TIP_GAP = 14;

const y = (v: number) => TOP + PLOT_H * (1 - Math.max(0, Math.min(100, v)) / 100);

function useWidth(ref: React.RefObject<HTMLElement>) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

export interface ChartPagination {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export interface SufficiencyChartProps {
  granularity: Granularity;
  buckets: SufficiencyBucket[];
  averageScore: number | null;
  medalScore: number;
  /** Text summary for screen readers, e.g. "Average 83, 48 medal days out of 72 logged." */
  summary: string;
  /** Dims the bars while a newer range is loading. */
  busy?: boolean;
  pagination?: ChartPagination;
}

export function SufficiencyChart({
  granularity,
  buckets,
  averageScore,
  medalScore,
  summary,
  busy,
  pagination,
}: SufficiencyChartProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const barRefs = useRef<(SVGGElement | null)[]>([]);
  const clipBase = useId().replace(/:/g, '');
  const containerW = useWidth(wrapRef);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [focusIdx, setFocusIdx] = useState<number | null>(null);

  // New data means new bars; an open tooltip would point at the wrong one.
  const [shownBuckets, setShownBuckets] = useState(buckets);
  if (shownBuckets !== buckets) {
    setShownBuckets(buckets);
    setHoverIdx(null);
    setFocusIdx(null);
  }

  const n = Math.max(buckets.length, 1);
  const width = Math.max(containerW || 640, PAD_L + PAD_R + n * MIN_SLOT[granularity]);
  const plotR = width - PAD_R;
  const slot = (plotR - PAD_L) / n;
  const barW = Math.min(BAR[granularity].max, slot * BAR[granularity].ratio);
  const rx = Math.min(7, barW / 2);
  const cxOf = (i: number) => PAD_L + slot * (i + 0.5);

  // When bars overflow, show the newest ones unless we are on an earlier paginated slice.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atLastPage = !pagination || pagination.page >= pagination.totalPages;
    el.scrollLeft = atLastPage ? el.scrollWidth : 0;
  }, [buckets, granularity, pagination?.page, pagination?.totalPages]);

  const activeIdx = hoverIdx ?? focusIdx;
  const active = activeIdx != null ? buckets[activeIdx] : null;

  const focusBar = (i: number) => barRefs.current[Math.max(0, Math.min(buckets.length - 1, i))]?.focus();

  const onKeyDown = (i: number) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setFocusIdx(focusIdx === i ? null : i);
    } else if (e.key === 'Escape') {
      setFocusIdx(null);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      focusBar(i + (e.key === 'ArrowRight' ? 1 : -1));
    }
  };

  let tipLeft = 0;
  if (activeIdx != null) {
    const cx = cxOf(activeIdx) - (scrollRef.current?.scrollLeft ?? 0);
    const visible = containerW || width;
    tipLeft = cx + TIP_GAP + TIP_W <= visible ? cx + TIP_GAP : Math.max(0, cx - TIP_GAP - TIP_W);
  }

  const medalY = y(medalScore);
  const avgY = averageScore != null ? y(averageScore) : null;
  // The medal label sits under its line; lift it above when the Avg pill would cover it.
  const medalLabelY = avgY != null && avgY > medalY && avgY - medalY < 30 ? medalY - 6 : medalY + 19;

  return (
    <div className="ns-panel">
      <div className="ns-chart-h">
        <div>
          <h4>
            <span>{CHART_TITLE[granularity]}</span>
            <InfoTip label="reading the chart" title={COPY.chartHelp.title} wide>
              <HelpBody {...COPY.chartHelp} />
            </InfoTip>
          </h4>
          <p>{chartHint(granularity)}</p>
        </div>
        <div className="ns-legend">
          {MACROS.map((m) => (
            <span key={m}>
              <MacroMark macro={m} />
              {MACRO_LABEL[m]}
            </span>
          ))}
          <span className="ns-legend-plain">
            <i className="ns-avg" aria-hidden />
            Average
          </span>
        </div>
      </div>

      <p className="ns-sr">
        {summary}
        {pagination && pagination.totalPages > 1 && ` ${COPY.chartPageStatus(pagination.page, pagination.totalPages)}`}
      </p>
      <div className={`ns-chart${busy ? ' ns-busy' : ''}`} ref={wrapRef}>
        <div className={`ns-chart-frame${pagination && pagination.totalPages > 1 ? ' ns-chart-frame-paged' : ''}`}>
          {pagination && pagination.totalPages > 1 && (
            <button
              type="button"
              className="ns-chart-arrow ns-chart-arrow-prev"
              aria-label={COPY.chartPagePrev}
              disabled={pagination.page <= 1 || busy}
              onClick={() => pagination.onPageChange(pagination.page - 1)}
            >
              <RiArrowLeftSLine aria-hidden />
            </button>
          )}
          <div className="ns-cscroll" ref={scrollRef} onScroll={() => setHoverIdx(null)}>
          <svg width={width} height={HEIGHT} role="group" aria-label={CHART_TITLE[granularity]}>
            {bandSpans().map(({ band, low, high }) => (
              <rect key={band.key} className={`ns-band-${band.key}`} x={PAD_L} y={y(high)} width={plotR - PAD_L} height={y(low) - y(high)} />
            ))}
            {TICKS.map((t) => (
              <g key={t}>
                <line className={t === 0 ? 'ns-axis' : 'ns-grid'} x1={PAD_L} x2={plotR} y1={y(t)} y2={y(t)} />
                <text className="ns-yl" x={PAD_L - 8} y={y(t) + 3} textAnchor="end">
                  {t}
                </text>
              </g>
            ))}
            <line className="ns-medal-line" x1={PAD_L} x2={plotR + 6} y1={medalY} y2={medalY} />
            <text className="ns-medal-l" x={plotR + 12} y={medalLabelY}>
              Medal {medalScore}
            </text>

            {buckets.map((b, i) => {
              const cx = cxOf(i);
              const x = cx - barW / 2;
              const label = axisLabel(granularity, b, i, buckets.length);
              const p = b.parts;
              const stackTotal = p ? p.protein + p.carbs + p.fats : 0;
              let base = TOP + PLOT_H;
              const band = bandFor(b.score);
              return (
                <g
                  key={b.from}
                  ref={(el) => (barRefs.current[i] = el)}
                  className={`ns-bar${activeIdx === i ? ' ns-bar-on' : ''}`}
                  tabIndex={0}
                  role="button"
                  aria-label={barAriaLabel(granularity, b, i)}
                  aria-describedby={activeIdx === i ? `${clipBase}-tip` : undefined}
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(null)}
                  onFocus={() => setFocusIdx(i)}
                  onBlur={() => setFocusIdx((f) => (f === i ? null : f))}
                  onClick={(e) => (e.currentTarget as SVGGElement).focus()}
                  onKeyDown={onKeyDown(i)}
                >
                  <rect className="ns-hit" x={PAD_L + slot * i} y={TOP} width={slot} height={HEIGHT - TOP} />
                  <rect className="ns-track" x={x} y={TOP} width={barW} height={PLOT_H} rx={rx} />
                  {p && b.score != null ? (
                    <>
                      <clipPath id={`${clipBase}-${i}`}>
                        <rect x={x} y={y(stackTotal)} width={barW} height={TOP + PLOT_H - y(stackTotal)} rx={rx} />
                      </clipPath>
                      <g clipPath={`url(#${clipBase}-${i})`}>
                        {MACROS.map((m) => {
                          const h = (PLOT_H * p[m]) / 100;
                          base -= h;
                          return (
                            <g key={m}>
                              <rect className={`ns-seg-${m}`} x={x} y={base} width={barW} height={h} />
                              <line className="ns-seg-gap" x1={x} x2={x + barW} y1={base} y2={base} />
                            </g>
                          );
                        })}
                      </g>
                    </>
                  ) : (
                    <text className="ns-nl" x={cx + 3.5} y={TOP + PLOT_H - 6} transform={`rotate(-90 ${cx + 3.5} ${TOP + PLOT_H - 6})`}>
                      {COPY.notLogged}
                    </text>
                  )}
                  {label.show && (
                    <>
                      <text className={`ns-xl${label.bold ? ' ns-xl-b' : ''}`} x={cx} y={TOP + PLOT_H + 17} textAnchor="middle">
                        {label.top}
                      </text>
                      {label.bottom && (
                        <text className="ns-xl ns-xl-2" x={cx} y={TOP + PLOT_H + 31} textAnchor="middle">
                          {label.bottom}
                        </text>
                      )}
                    </>
                  )}
                </g>
              );
            })}

            {avgY != null && (
              <g className="ns-avg-g">
                <line className="ns-avg-line" x1={PAD_L} x2={plotR + 8} y1={avgY} y2={avgY} />
                <rect className="ns-avg-pill" x={plotR + 8} y={avgY - 11} width={52} height={22} rx={11} />
                <text className="ns-avg-t" x={plotR + 34} y={avgY + 4} textAnchor="middle">
                  Avg {averageScore}
                </text>
              </g>
            )}
          </svg>
          </div>
          {pagination && pagination.totalPages > 1 && (
            <button
              type="button"
              className="ns-chart-arrow ns-chart-arrow-next"
              aria-label={COPY.chartPageNext}
              disabled={pagination.page >= pagination.totalPages || busy}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
            >
              <RiArrowRightSLine aria-hidden />
            </button>
          )}
        </div>

        {active && activeIdx != null && (
          <div className="ns-tip" id={`${clipBase}-tip`} role="tooltip" style={{ left: tipLeft, width: TIP_W }}>
            <BarTooltip granularity={granularity} bucket={active} index={activeIdx} />
          </div>
        )}
      </div>
    </div>
  );
}

function BarTooltip({ granularity, bucket, index }: { granularity: Granularity; bucket: SufficiencyBucket; index: number }) {
  const band = bandFor(bucket.score);
  return (
    <>
      <div className="ns-tip-h">
        <b>{tooltipTitle(granularity, bucket, index)}</b>
        {band && <BandPill band={band} />}
      </div>
      {bucket.score == null || !bucket.macros ? (
        <p className="ns-tip-nl">{COPY.notLogged}</p>
      ) : (
        <>
          <div className="ns-tip-score">
            <b className={`ns-ink-${band?.key}`}>{bucket.score}</b> of 100
          </div>
          {MACROS.map((m) => {
            const { eaten, target } = bucket.macros![m];
            const status = target != null ? macroStatus(eaten, target) : 'in';
            return (
              <div className="ns-tip-row" key={m}>
                <MacroMark macro={m} />
                <span className="ns-tip-n">{MACRO_LABEL[m]}</span>
                <span className="ns-tip-bar">
                  <i className={`ns-tip-${status}`} style={{ width: `${target != null ? ratioPct(eaten, target) : 100}%` }} />
                </span>
                <span className="ns-tip-v">
                  {Math.round(eaten)}
                  {target != null && `/${Math.round(target)}`} g
                </span>
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
